import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  Signal,
  effect,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { StateModule } from "projects/shared-state/lib/state.module";
import { StateService } from "projects/shared-state/lib/state.service";
import { PromptConfig, Workflow } from "projects/shared-state/lib/types/types";
import { WorkflowState } from "projects/shared-state/public-api";
import { JsonValueByKeyPipe } from "projects/shared-new/src/lib/utils/json-value-by-key.pipe";
import { Subscription, tap } from "rxjs";
import { UtilsService } from "projects/shared-new/src/lib/utils/utils.service";

@Component({
  standalone: true,
  selector: "post-goal",
  templateUrl: "./post-goal.component.html",
  styleUrls: ["./post-goal.component.scss"],
  imports: [
    StateModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    JsonValueByKeyPipe,
  ],
})
export class PostGoalComponent implements OnInit {
  @Output() disablePageEvent = new EventEmitter<string>();
  stateService = inject(StateService);
  utilsService = inject(UtilsService);

  workflowForm: FormGroup = new FormGroup({
    postGoal: new FormControl("", [Validators.required]),
    postGoalSuggestions: new FormControl("", []),
  });

  charCount: Subscription;
  postGoalCount: number = 0;
  postGoalMax: number = 500;

  stateSignal$: Signal<{
    workflow: WorkflowState;
  }> = this.stateService.getAppState();

  constructor() {
    effect(() => {
      this.charCount = this.workflowForm.valueChanges
        .pipe(
          tap((workflowForm) => {
            this.postGoalCount = workflowForm.postGoal?.length;
          }),
        )
        .subscribe();

      const workflow: Workflow = this.stateSignal$().workflow.workflow;
      if (workflow) {
        this.workflowForm.patchValue({
          postGoal: workflow.promptWorkflow?.userInputs?.postGoal || "",
          postGoalSuggestions:
            workflow.promptWorkflow?.aiResults?.postDescription || "",
        });
      }
    });
  }

  ngOnInit(): void {
    if (this.stateSignal$().workflow.workflow.name === "New LinkedIn Post") {
      try {
        this.stateService.saveWorkflow(
          {
            ...this.stateSignal$().workflow.workflow,
            name: JSON.parse(
              this.utilsService.cleanJsonString(
                this.stateSignal$().workflow.workflow.promptWorkflow.aiResults
                  .postDescription,
              ),
            )["post_name"],
          },
          this.stateSignal$().workflow.workflow.uid,
        );
      } catch {}
    }
  }

  askGenAi(): void {
    const promptConfiguration: PromptConfig = {
      promptIdentifier: "post_goal",
      replacementDict: {
        postGoal: this.workflowForm.value.postGoal,
        postDescription:
          this.stateSignal$().workflow.workflow.promptWorkflow.userInputs
            .postDescription,
        profileSummary:
          this.stateSignal$().workflow.profile.appProfile.profileSummary || "",
        fileContent:
          this.stateSignal$().workflow.workflow.promptWorkflow.aiResults
            .fileSummary,
      },
    };

    this.saveWorkflow();
    this.stateService.askGenAiApi(
      promptConfiguration,
      "postGoal",
      "postProcess",
    );
    this.disablePageEvent.emit("postGoal");
  }

  patchPostGoal(value: string): void {
    this.workflowForm.patchValue({
      postGoal: value || "",
    });
  }

  saveWorkflow(): void {
    this.stateService.saveWorkflow(
      {
        ...this.stateSignal$().workflow.workflow,
        name: this.stateSignal$().workflow.workflow.name || "new post",
        status: "draft",
        step: 2,
        activeStep: 3,
        promptWorkflow: {
          userInputs: {
            ...this.stateSignal$().workflow.workflow.promptWorkflow.userInputs,
            postGoal: this.workflowForm.value.postGoal,
          },
          aiResults: {
            ...this.stateSignal$().workflow.workflow.promptWorkflow.aiResults,
            postGoal: "",
            postFinal: "",
          },
          uploadedFiles: [
            ...this.stateSignal$().workflow.workflow.promptWorkflow
              .uploadedFiles,
          ],
        },
      },
      this.stateSignal$().workflow.workflow.uid,
    );
  }

  selectPostGoal(postGoal: string): void {
    this.workflowForm.patchValue({ postGoal: postGoal });
  }

  ngOnDestroy(): void {
    if (!this.charCount.closed) {
      this.charCount.unsubscribe();
    }
  }
}
